import { useCallback } from "react";
import { taskListFilterOutputMappings } from "../../components/task-list/lib/constants";

export const operator = {
    eq: "EQ",
    neq: "NEQ",
    like: "LIKE",
    gte: "GTE",
    lte: "LTE",
    in: "IN",
    inlike: "INLIKE",
    startsWith: "STARTSWITH",
    none: "NONE",
};

const dateParams = ["publishDate", "receivedOn", "due", "createDate", "updatedOn", "shipment.dueDate", "startDate"];

const operatorSelectorStrategy = {
    binTrackingNumber: (value) => (Array.isArray(value) ? operator.inlike : operator.like),
    upc: (value) => (Array.isArray(value) ? operator.inlike : operator.like),
    client: (value) => (Array.isArray(value) ? operator.in : operator.like),
    gdnClient: (value) => (Array.isArray(value) ? operator.in : operator.like),
    packageLevelName: (value) => (Array.isArray(value) ? operator.in : operator.like),
    cxhClient: (value) => (Array.isArray(value) ? operator.in : operator.like),
    shipmentType: (value) => (value === "-1" ? operator.none : operator.eq),
    "shipment.type": (value) => (value === "-1" ? operator.none : operator.eq),
    state: (value) => (value === "all" ? operator.none : operator.eq),
    priority: () => operator.eq,
    "locationInfo.location": () => operator.in,
    location: (value) => (Array.isArray(value) ? operator.in : operator.eq),
    isRush: () => operator.eq,
    hasPremiumEditing: () => operator.eq,
    isFoodService: () => operator.eq,
    isFinalized: (value) => (value === "all" ? operator.none : operator.eq),
    status: (value) => (value === "all" ? operator.none : operator.eq),
    duplicateMatch: () => operator.eq,
    imagesNeedClientApproval: () => operator.eq,
    assignmentGroupKey: () => operator.eq,
    "assignedUserInfo.userId": () => operator.eq,
    matchedProductTaskContextKey: (value) => (value === "true" ? operator.neq : operator.eq),
    correlationId: (value) => (Array.isArray(value) ? operator.in : operator.eq),
    "context.locationDataSource": () => operator.eq,
};

const valueSelectorStrategy = {
    matchedProductTaskContextKey: () => null,
};

const includesDateParam = (id) => {
    return dateParams.some((dateKey) => id.includes(dateKey));
};

export const useFilterOperators = (filtersModel, outputMapper, taskType) => {
    const prepareFilters = (filterAccumulator, { id, isDate, mapTo, value, values, overrideOperator }, isAdvancedFilter) => {
        // BE expects different filter id name than UI uses
        const mappedId = taskListFilterOutputMappings[id];
        const reqId = mappedId || id;
        const getOperator = operatorSelectorStrategy[reqId];
        const getValue = valueSelectorStrategy[reqId];

        if (!isAdvancedFilter && includesDateParam(id) && value) {
            if (value.filterFromDate) {
                filterAccumulator.push({
                    column: id,
                    value: value.filterFromDate,
                    operator: operator.gte,
                });
            }

            if (value.filterToDate) {
                filterAccumulator.push({
                    column: id,
                    value: value.filterToDate,
                    operator: operator.lte,
                });
            }
        } else {
            if (isAdvancedFilter) {
                let correspondingFilterName = Object.keys(filtersModel).find((filterKey) => {
                    return filtersModel[filterKey].name === id;
                });

                if (!correspondingFilterName) {
                    const mappedParam = outputMapper && outputMapper[id];

                    if (!mappedParam) {
                        throw new Error(`Advanced filter not listed: ${id}`);
                    }

                    correspondingFilterName = mappedParam;
                }

                // TODO: Technical debt, refactor to unify dates in if if statement from line 114
                if (id === "publishDateStart" || id === "startDate") {
                    filterAccumulator.push({
                        column: mapTo,
                        value,
                        operator: operator.gte,
                    });

                    return filterAccumulator;
                }

                // TODO: Technical debt, refactor to unify dates in if if statement from line 114
                if (id === "publishDateEnd" || id === "endDate") {
                    filterAccumulator.push({
                        column: mapTo,
                        value,
                        operator: operator.lte,
                    });

                    return filterAccumulator;
                }

                if (dateParams.indexOf(mapTo) !== -1) {
                    filterAccumulator.push({
                        column: mapTo,
                        value,
                        operator: id.includes("start") ? operator.gte : operator.lte,
                    });

                    return filterAccumulator;
                }

                const isFilterMulti = filtersModel[correspondingFilterName].isMulti;
                const hasDefinedOperator = !!getOperator;
                let filter = {
                    column: id,
                    // TODO: FIX all operators should have a strategy defined
                    operator: hasDefinedOperator ? getOperator(isFilterMulti ? [value] : value) : isFilterMulti ? operator.inlike : operator.like,
                };

                if (isFilterMulti) {
                    filter.values = value.split(",");
                } else if (Array.isArray(value) && value.length) {
                    // prevent adding empty arrays to payload
                    filter.values = value;
                } else {
                    filter.value = value;
                }

                if (overrideOperator) {
                    filter.operator = overrideOperator;
                }

                filterAccumulator.push(filter);

                return filterAccumulator;
            }

            let filter = {
                column: id,
            };

            if (taskType === "duplicatestaging" && id === "fullName") {
                filter.column = "assignedUserInfo.userId";
            }

            if (id === "upc" || id === "context.upc") {
                // edge case, UPC is set outside advanced filtering AND table, it needs to handle multiple UPCs
                filter.values = value.split(",");
                filter.operator = operator.inlike;
            } else {
                filter.value = getValue ? getValue(value) : value;
                filter.operator = getOperator ? getOperator(value) : operator.like;
            }

            filterAccumulator.push(filter);
        }

        return filterAccumulator;
    };

    const filterNoneOperator = (filter) => filter.operator !== operator.none;

    return useCallback((filters, hasMultipleValues) => {
        return filters.reduce((acc, filter) => prepareFilters(acc, filter, hasMultipleValues), []).filter(filterNoneOperator);
    }, []);
};
